import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'src/store';
import ComboBox, {
  ChannelMemberSelectComboBoxOption,
  ComboBoxOption,
} from 'src/components/Select/ComboBox';
import { Company, Client } from 'src/store/clients/types';
import * as UserUtils from 'src/utils/UserUtils';
import { useGetPortalConfigQuery } from 'src/services/api';
import { GetCurrentUser } from 'src/store/users/reducers';

export interface ChannelMemberSelectProps {
  id: string;
  onChange: (
    ev: React.ChangeEvent,
    vals: ChannelMemberSelectComboBoxOption[],
  ) => void;
  memberIds: string[] | string;
  error?: boolean;
  helperText?: any;
  disabled?: boolean;
  autoFocus?: boolean;
  multiple?: boolean;
  excludeEmptyCompany?: boolean;
}

/**
 * This component lets you select members for a channel (file, messages, extension, etc.)
 * where you can select multiple clients from the same company or an entire company.
 */
export const ChannelMemberSelect: React.FC<ChannelMemberSelectProps> = ({
  id,
  onChange,
  memberIds,
  error,
  helperText,
  disabled = false,
  multiple = true,
  autoFocus = true,
  excludeEmptyCompany = false,
}) => {
  const { data: portalConfigurations } = useGetPortalConfigQuery();
  const areCompaniesDisabled =
    portalConfigurations?.structFields?.disableCompanies;
  const companies = useSelector(
    (state: RootState) => state.clients.activeCompanies,
  );
  const activeClients = useSelector(
    (state: RootState) => state.clients.activeClients,
  );

  const authenticatedUserID = useSelector(
    (state: RootState) =>
      GetCurrentUser({
        usersState: state.users,
        currentUserId: state.user.id,
        isClient: state.user.isClient,
      })?.id || '',
  );

  const [dropdownOptions, setDropdownOptions] = React.useState<
    ChannelMemberSelectComboBoxOption[] | null
  >(null);

  const [selectedOptions, setSelectedOptions] = React.useState<
    ComboBoxOption[] | ComboBoxOption | null
  >(multiple ? [] : null);

  // if excludeEmptyCompany prop = true then filter out the companies without any clients
  const validCompanies = React.useMemo(() => {
    if (!excludeEmptyCompany) {
      return companies;
    }

    const activeCompanies: Company[] = [];
    const companyIdToNumbOfAvailableClients = new Map<string, number>();
    activeClients.forEach((c) => {
      if (!UserUtils.isUserEnabled(c)) {
        return;
      }

      const numbCompanies = companyIdToNumbOfAvailableClients.get(
        c.fields.companyId,
      );
      if (numbCompanies !== undefined) {
        companyIdToNumbOfAvailableClients.set(
          c.fields.companyId,
          numbCompanies + 1,
        );
      } else {
        companyIdToNumbOfAvailableClients.set(c.fields.companyId, 1);
      }
    });

    companies.forEach((c) => {
      const enabledUsers = companyIdToNumbOfAvailableClients.get(c.id);
      if (enabledUsers !== undefined) {
        if (enabledUsers > 0) {
          activeCompanies.push(c);
        }
      }
    });
    return activeCompanies;
  }, [companies, activeClients, excludeEmptyCompany]);

  /**
   * This method formats options so they can be rendered in in the combobox dropdown
   */
  const allDropdownOptions = React.useMemo(() => {
    // only get companies where the current user has visibility
    const filteredCompanyList: Company[] = validCompanies.filter((c) => {
      const assignedUsers: Record<string, boolean> | undefined =
        c.additionalFields?.assignedUserIDs;
      if (assignedUsers) {
        return assignedUsers[authenticatedUserID];
      }
      return false;
    });

    const optionList = [...filteredCompanyList, ...activeClients];
    return optionList
      .map((item: Company | Client) => {
        if (UserUtils.hasUserNameFields(item)) {
          // is client user entity
          const { givenName, familyName, picture, fallbackColor, companyId } =
            item.fields;

          const { getstreamId } = item;
          return {
            id: item.id,
            getstreamId,
            type: 'client',
            label: `${givenName} ${familyName}`,
            avatar: picture,
            fallbackColor,
            companyId,
          };
        }
        const companyFields = (item as Company).fields;
        return {
          id: item.id,
          type: 'company',
          label: companyFields.name,
          avatar: companyFields.avatarImageURL,
          fallbackColor: companyFields.fallbackColor,
        };
      })
      .sort((a, b) =>
        a.label.localeCompare(b.label, 'en', { sensitivity: 'base' }),
      );
  }, [validCompanies, activeClients, authenticatedUserID]);
  const updateMultiSelectDropdownOptions = (
    members: string[],
    dropDownSrc: ChannelMemberSelectComboBoxOption[],
  ) => {
    setSelectedOptions(dropDownSrc.filter((opt) => members.includes(opt.id)));

    if (!selectedOptions) {
      return;
    }

    if (memberIds.length === 0) {
      // if there are no IDs selected the full option list should be shown
      setDropdownOptions(dropDownSrc);
    } else if (
      Array.isArray(selectedOptions) &&
      selectedOptions.length &&
      selectedOptions.every((opt) => opt.type === 'company')
    ) {
      // if a company is selected we want to prevent another combo box option from being selected
      setDropdownOptions([]);
    } else if (Array.isArray(selectedOptions) && selectedOptions.length) {
      // selected clients determine which clients we show in the dropdown (must be same company)
      const selectedClientCompanyIDs = selectedOptions.map(
        (opt) => opt.companyId,
      );
      setDropdownOptions(
        dropDownSrc.filter(
          (option) =>
            !memberIds.includes(option.id) &&
            option.type !== 'company' &&
            selectedClientCompanyIDs.includes(option.companyId),
        ) || [],
      );
    }
  };

  const updateSingleSelectDropdownOptions = (
    members: string,
    dropDownSrc: ChannelMemberSelectComboBoxOption[],
  ) => {
    setSelectedOptions(
      allDropdownOptions.find((opt) => opt.id === members) || null,
    );

    setDropdownOptions(dropDownSrc);
  };

  // each time values change we update remaining choices accordingly
  React.useEffect(() => {
    let dropdownEntries = allDropdownOptions;
    // Behind feature flag, filter out companies if they are disabled
    if (areCompaniesDisabled) {
      dropdownEntries = dropdownEntries.filter((opt) => opt.type !== 'company');
    }
    if (multiple && memberIds && Array.isArray(memberIds)) {
      updateMultiSelectDropdownOptions(memberIds, dropdownEntries);
    } else {
      updateSingleSelectDropdownOptions(
        Array.isArray(memberIds) ? memberIds[0] : memberIds,
        dropdownEntries,
      );
    }
  }, [memberIds, allDropdownOptions, areCompaniesDisabled]);

  return dropdownOptions ? (
    <ComboBox
      textFieldVariant="medium"
      disabled={disabled}
      disabledUserIDs={[]}
      error={error}
      helperText={helperText}
      id={id}
      label="Select client(s) or company"
      placeholder="Type to find clients or companies"
      showPlaceholder
      onChange={(
        event: React.ChangeEvent<any>,
        vals: ChannelMemberSelectComboBoxOption[],
      ) => {
        setSelectedOptions(vals);
        onChange(event, vals);
      }}
      options={dropdownOptions || []}
      values={selectedOptions}
      additionalComboBoxProps={{
        inputProps: {
          autoFocus,
        },
      }}
      withAvatars
      avatarSize="18small"
      hideSelectedItems
      multiple={multiple}
    />
  ) : null;
};
